import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { hubApi } from "../../lib/hubCustomerApi";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Loader2, Briefcase, CheckCircle2, Circle, MessageSquare, Building2, ArrowLeft,
  Send, Clock, Rocket, PartyPopper, HourglassIcon, Inbox,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  to_start: { label: "À démarrer", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Clock, pct: 10 },
  in_progress: { label: "En cours", color: "bg-sky-100 text-sky-700 border-sky-200", icon: Rocket, pct: 60 },
  awaiting_client: { label: "Votre validation", color: "bg-violet-100 text-violet-700 border-violet-200", icon: HourglassIcon, pct: 85 },
  completed: { label: "Livré ✓", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: PartyPopper, pct: 100 },
};

export default function CustomerProjectsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    hubApi.get("/hub-customer/projects").then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);

  if (rows === null) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400"/></div>;

  return (
    <div data-testid="customer-projects-list">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-sky-600 mb-2">
          <Briefcase className="w-3 h-3"/> Mes projets
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-1">Suivi de mes projets</h1>
        <p className="text-sm text-slate-500">
          {rows.length === 0 ? "Aucun projet en cours."
            : `${rows.length} projet${rows.length > 1 ? "s" : ""} — suivez l'avancement en temps réel.`}
        </p>
      </div>

      {rows.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 bg-white/60">
          <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
          <p className="text-sm text-slate-500">Un projet sera automatiquement créé ici dès que vous validerez un devis.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {rows.map(p => {
            const meta = STATUS_META[p.status] || STATUS_META.to_start;
            const Icon = meta.icon;
            return (
              <Link key={p.id} to={`/mon-espace/projets/${p.id}`} className="block">
                <Card className="p-5 hover:shadow-lg transition" data-testid={`project-row-${p.id}`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border ${meta.color} shrink-0`}>
                      <Icon className="w-5 h-5"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[11px] font-bold text-slate-400">{p.number}</span>
                        <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                      </div>
                      <div className="font-black text-lg text-slate-900 truncate">{p.title}</div>
                      {p.site_name && <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5"><Building2 className="w-3 h-3"/>{p.site_name}</div>}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[11px] mb-1.5">
                          <span className="text-slate-500">Progression</span>
                          <span className="font-black text-sky-700">{p.progress_pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all duration-500" style={{ width: `${p.progress_pct}%` }}/>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CustomerProjectDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { setP((await hubApi.get(`/hub-customer/projects/${id}`)).data); }
    catch { toast.error("Projet introuvable"); nav("/mon-espace/projets"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await hubApi.post(`/hub-customer/projects/${id}/updates`, { message: msg });
      setMsg(""); load();
      toast.success("Message envoyé");
    } catch { toast.error("Erreur"); }
    finally { setSending(false); }
  };

  if (!p) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400"/></div>;

  const meta = STATUS_META[p.status] || STATUS_META.to_start;

  return (
    <div className="max-w-4xl" data-testid="customer-project-detail">
      <Link to="/mon-espace/projets" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-3.5 h-3.5"/> Tous mes projets
      </Link>

      {/* Hero */}
      <div className="rounded-3xl overflow-hidden mb-5 bg-gradient-to-br from-[#0F2744] to-[#0B1E36] text-white p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <meta.icon className="w-6 h-6 text-sky-300"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] opacity-70 font-bold">{p.number}</div>
            <h1 className="text-2xl font-black mb-2">{p.title}</h1>
            <Badge className={meta.color}>{meta.label}</Badge>
          </div>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs mb-2 opacity-90">
            <span>Avancement</span>
            <span className="font-black text-lg">{p.progress_pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all duration-700" style={{ width: `${p.progress_pct}%` }}/>
          </div>
        </div>
      </div>

      {/* Jalons */}
      <Card className="p-5 mb-4">
        <h2 className="font-black text-lg mb-3">Étapes du projet</h2>
        {(p.milestones || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">Les étapes apparaîtront ici dès que l'agence les aura définies.</div>
        ) : (
          <div className="space-y-2">
            {(p.milestones || []).map((m, i) => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                <div className="shrink-0">
                  {m.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-500"/> : <Circle className="w-5 h-5 text-slate-300"/>}
                </div>
                <div className="flex-1">
                  <div className={`font-semibold text-sm ${m.status === "done" ? "text-slate-500 line-through" : "text-slate-900"}`}>
                    {i + 1}. {m.label}
                  </div>
                  {m.done_at && <div className="text-[10px] text-emerald-600">✓ Validé le {new Date(m.done_at).toLocaleDateString("fr-FR")}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Messages */}
      <Card className="p-5">
        <h2 className="font-black text-lg mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4"/>Messages & mises à jour</h2>
        <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
          {(p.updates || []).slice().reverse().map(u => (
            <div key={u.id} className={`p-3 rounded-xl text-sm ${
              u.author === "client" ? "bg-violet-50 border border-violet-100" :
              u.author === "system" ? "bg-slate-100 italic text-slate-500" :
              "bg-sky-50 border border-sky-100"
            }`}>
              <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-wider font-bold opacity-70">
                <span>{u.author === "client" ? "👤 Vous" : u.author === "system" ? "⚙️ Système" : "🛠 Votre agence"}</span>
                <span>{new Date(u.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <div>{u.message}</div>
            </div>
          ))}
          {(p.updates || []).length === 0 && <div className="text-xs text-slate-400 italic p-3 text-center">Aucun message pour l'instant.</div>}
        </div>
        <div className="border-t pt-4">
          <div className="flex gap-2">
            <Textarea rows={2} value={msg} onChange={e => setMsg(e.target.value)} placeholder="Question ou retour pour l'agence…" data-testid="project-client-msg"/>
            <Button onClick={send} disabled={sending} size="sm" className="rounded-full self-end bg-slate-900 hover:bg-slate-800" data-testid="project-client-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
